import type { RefObject } from 'react';
import { flushPendingNoteAutosave } from './flushPendingNoteAutosave.util';
import type { NoteFieldRef } from './NoteField';

/** Params for {@link handleNoteFieldClose}. */
export interface HandleNoteFieldCloseParams {
  /** Create flows clear the draft; edit flows flush debounced parent PATCH autosave. */
  mode: 'create' | 'edit';
  /** Whether the parent form has unsaved non-note changes. */
  isDirty: boolean;
  noteFieldRef: RefObject<NoteFieldRef | null>;
  /** Unsaved-changes confirm dialog; receives the close continuation. */
  handleUnsavedChanges: (onConfirm: () => void | Promise<void>) => void;
  /** Runs after note flush/clear (e.g. close dialog). */
  finishClose: () => void;
}

/**
 * Flushes edit autosave or clears a create draft before close.
 *
 * @param mode - Create clears local draft; edit awaits parent PATCH autosave.
 * @param noteFieldRef - Ref to the dialog's `NoteField`.
 * @returns True when it is safe to dismiss (autosave did not fail).
 */
async function flushEditOrClearCreateDraft(
  mode: 'create' | 'edit',
  noteFieldRef: RefObject<NoteFieldRef | null>
): Promise<boolean> {
  if (mode === 'edit') {
    return flushPendingNoteAutosave(noteFieldRef);
  }

  noteFieldRef.current?.clearDraft();
  return true;
}

/**
 * Closes a note-bearing surface after flushing edit autosave or clearing a create draft.
 * Matches visit/hospitalization close behavior for item-linked notes.
 *
 * @param params - Note mode, dirty state, refs, and close callback.
 */
export function handleNoteFieldClose({
  mode,
  isDirty,
  noteFieldRef,
  handleUnsavedChanges,
  finishClose,
}: HandleNoteFieldCloseParams): void {
  const tryClose = async () => {
    const ok = await flushEditOrClearCreateDraft(mode, noteFieldRef);
    if (ok) {
      finishClose();
    }
  };

  if (isDirty) {
    void handleUnsavedChanges(tryClose);
    return;
  }

  void tryClose();
}
